import { useCallback, useEffect, useState } from "react";
import { Pencil, RotateCcw, Save } from "lucide-react";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";

/** Rinomina titoli di pagine senza griglia (elenchi e tabelle), con la stessa
 *  modalità Modifica delle dashboard. I titoli sono salvati nel layout della pagina. */
export function useTitoliPagina(pagina) {
  const { puo } = useAuth();
  const abilitato = puo("modifica_dashboard");
  const [titoli, setTitoli] = useState({});
  const [modifica, setModifica] = useState(false);

  useEffect(() => {
    api.get(`/layout/${pagina}`).then((r) => setTitoli(r.data.titoli || {})).catch(() => {});
  }, [pagina]);

  const salva = useCallback(async () => {
    try {
      await api.put(`/layout/${pagina}`, { titoli });
      toast.success("Titoli salvati");
      setModifica(false);
    } catch { toast.error("Salvataggio non riuscito"); }
  }, [pagina, titoli]);

  const ripristina = async () => {
    await api.delete(`/layout/${pagina}`);
    setTitoli({});
    toast.success("Titoli originali ripristinati");
  };

  const T = ({ chiave, originale, className = "" }) => {
    const [aperto, setAperto] = useState(false);
    const [txt, setTxt] = useState(titoli[chiave] || originale);
    const testo = titoli[chiave] || originale;
    if (!modifica) return <span className={className}>{testo}</span>;
    if (aperto) {
      const conferma = () => {
        setTitoli({ ...titoli, [chiave]: txt.trim() });
        setAperto(false);
      };
      return (
        <span className="inline-flex items-center gap-1">
          <input autoFocus value={txt} data-testid={`rinomina-${chiave}-input`}
            onChange={(e) => setTxt(e.target.value)}
            onKeyDown={(e) => { if (e.key === "Enter") conferma(); if (e.key === "Escape") setAperto(false); }}
            className="text-sm border border-sky-400 rounded px-2 py-1 bg-white text-slate-800
              normal-case tracking-normal w-56" />
          <button type="button" onClick={conferma} data-testid={`rinomina-${chiave}-ok`}
            className="text-xs font-semibold text-emerald-700 hover:text-emerald-900">OK</button>
          <button type="button" data-testid={`rinomina-${chiave}-reset`}
            onClick={() => { setTitoli({ ...titoli, [chiave]: "" }); setAperto(false); }}
            className="text-xs text-slate-400 hover:text-slate-700">originale</button>
        </span>
      );
    }
    return (
      <span className={`inline-flex items-center gap-1.5 ${className}`}>
        {testo}
        <button type="button" title="Rinomina" data-testid={`rinomina-${chiave}`}
          onClick={() => { setTxt(testo); setAperto(true); }}
          className="text-slate-400 hover:text-sky-700 transition-colors duration-200">
          <Pencil size={13} />
        </button>
      </span>
    );
  };

  const Barra = () => {
    if (!abilitato) return null;
    return (
      <div className="flex flex-wrap items-center gap-2 mb-4">
        <button type="button" onClick={() => setModifica((v) => !v)} data-testid="btn-modifica-titoli"
          className={`inline-flex items-center gap-2 text-sm px-4 py-2 rounded-md font-semibold border-2
            transition-colors duration-200 ${modifica
              ? "bg-sky-600 border-sky-600 text-white hover:bg-sky-700"
              : "bg-white border-sky-500 text-sky-700 hover:bg-sky-50"}`}>
          <Pencil size={15} /> {modifica ? "Chiudi modifica" : "Modifica"}
        </button>
        {modifica && (
          <>
            <button type="button" onClick={salva} data-testid="btn-salva-titoli"
              className="inline-flex items-center gap-2 text-sm px-4 py-2 rounded-md bg-emerald-600
                text-white font-semibold hover:bg-emerald-700 transition-colors duration-200">
              <Save size={15} /> Salva titoli
            </button>
            <button type="button" onClick={ripristina} data-testid="btn-ripristina-titoli"
              className="inline-flex items-center gap-2 text-sm px-4 py-2 rounded-md border border-slate-300
                bg-white text-slate-600 hover:border-slate-500 transition-colors duration-200">
              <RotateCcw size={15} /> Ripristina
            </button>
            <span className="text-xs text-slate-500">
              Clicca la matitina accanto a un titolo per rinominarlo
            </span>
          </>
        )}
      </div>
    );
  };

  return { T, Barra, modifica };
}
